import React from "react";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Body1, Header1, SolidButton } from "@iventis/styles";
import { User } from "@iventis/domain-model/model/user";
import { DigitalTwinLicence } from "@iventis/domain-model/model/digitalTwinLicence";
import { useQuery } from "@tanstack/react-query";
import { LoadingComponent } from "@iventis/components";
import { SUPPORT_EMAIL } from "@iventis/utilities";
import { Container, ErrorIcon } from "./digital-twin-components";
import LicenceTable from "./instance-table";
import { DigitalTwinConnectionError } from "./digitial-twin-types";

export type DigitalTwinOccupiedComponentProps = {
    getLicences: () => Promise<DigitalTwinLicence[]>;
    getUser: (id: string) => Promise<User>;
    error?: DigitalTwinConnectionError;
    onRetryClick: () => Promise<void>;
};

/**
 * A loading component for the digital twin.
 */
export const DigitalTwinOccupiedComponent: React.FunctionComponent<DigitalTwinOccupiedComponentProps> = ({ error, getLicences, getUser, onRetryClick }) => {
    const translate = useIventisTranslate();

    const [isRetrying, setIsRetrying] = React.useState(false);

    const handleRetry = async () => {
        setIsRetrying(true);
        try {
            await onRetryClick();
        } finally {
            setIsRetrying(false);
        }
    };

    // Get the instance data
    const { data: licences, isFetched: licencesLoaded } = useQuery(["digital-twin-licences"], getLicences, { refetchInterval: 10000, enabled: error === "occupied" });
    // Once we have the instance data, get the user data for all instances. We have to wait for the instance data to be loaded first.
    const { data: users, isFetched: usersLoaded } = useQuery(
        ["digital-twin-users", licences],
        async () => {
            if (licences) {
                return Promise.all(licences.filter((licence) => licence.userId != null).map((licence) => getUser(licence.userId)));
            }
            return [];
        },
        { enabled: licencesLoaded && error === "occupied" }
    );

    // We want to check the instance's lastConnectedUserId and map that to the user
    // We want to display the instance id, status and user in the table
    const instanceToUserMap = licences?.reduce((acc, licence) => ({ ...acc, [licence.id]: users?.find((user) => user.id === licence.userId) }), {} as Record<string, User>);

    // Determine the appropriate header and body text based on error type
    const getErrorContent = () => {
        switch (error) {
            case "insufficent-resources":
                return {
                    header: translate(Content.map9.digitalTwin.occupiedPopup.header),
                    body: translate(Content.errors.digital_twin.capacity, { supportEmail: SUPPORT_EMAIL }),
                };
            case "other":
                return {
                    header: translate(Content.errors.error),
                    body: translate(Content.errors.problem_with_our_servers),
                };
            default:
                return {
                    header: translate(Content.map9.digitalTwin.occupiedPopup.header),
                    body: translate(Content.map9.digitalTwin.occupiedPopup.message, { supportEmail: SUPPORT_EMAIL }),
                };
        }
    };

    return (
        <Container data-testid="digital-twin-error-screen">
            <ErrorIcon />
            <Header1>{getErrorContent().header}</Header1>
            <Body1>{getErrorContent().body}</Body1>
            {isRetrying ? (
                <div>
                    <LoadingComponent />
                </div>
            ) : (
                <SolidButton onClick={handleRetry}>{translate(Content.common.buttons.retry)}</SolidButton>
            )}
            {error === "occupied" && (
                <div>
                    {usersLoaded && <LicenceTable licences={licences} licenceToUserMap={instanceToUserMap} />}
                    {!usersLoaded && <LoadingComponent />}
                </div>
            )}
        </Container>
    );
};
